import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Zap,
  TrendingUp,
  ShieldCheck,
  ArrowRight,
  Sparkles,
  Radar,
  BookOpen,
  Rss,
  Database,
  Brain,
  BarChart2,
  CheckCircle2,
  Clock,
  DollarSign,
  Layers,
  Plus,
  X,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { confetti } from 'dom-confetti';
import { useLanguage } from '../hooks/useLanguage';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  'https://ai-workshop-llc-backend.onrender.com';

// Route every tool-Visit click on Discover through the affiliate tracker
// (/api/launch/{toolId}). Same redirect endpoint the report-side ToolsTab
// uses — this turns organic Discover traffic into measurable + affiliate-
// attributed behavior from buyer #1. Falls through to the raw URL when
// the tool has no ID (defensive — should never happen for catalog tools
// but covers manually-added entries that lack an id).
const buildDiscoverLaunchUrl = (tool, fallbackUrl) => {
  const id = tool && (tool.id || tool.tool_id);
  if (!id) return fallbackUrl || null;
  return `${API_BASE}/api/launch/${encodeURIComponent(id)}?utm_source=discover_hub`;
};

// Animated SVG score ring
const ScoreRing = ({ score = 0, max = 10 }) => {
  const pct = Math.min(score / max, 1);
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = pct * circ;
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg viewBox="0 0 64 64" className="w-20 h-20 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#1e293b" strokeWidth="4" />
        <motion.circle
          cx="32" cy="32" r={r} fill="none"
          stroke="#3b82f6" strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${circ}`}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - dash }}
          transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-[#3b82f6] leading-none">{score}</span>
        <span className="text-[9px] text-slate-500 leading-none mt-0.5">/ {max}</span>
      </div>
    </div>
  );
};

// Known native integrations — matched by name (case-insensitive) so both template and catalog tools work
const SYNERGY_PAIRS_NAMES = [
  ['hubspot', 'zapier'], ['hubspot', 'calendly'],
  ['klaviyo', 'tidio'], ['klaviyo', 'postscript'],
  ['manychat', 'mailchimp'], ['opentable', 'mailchimp'],
];

const hasSynergy = (tools) => {
  const names = tools.map(t => t.name?.toLowerCase() ?? '');
  return SYNERGY_PAIRS_NAMES.some(([a, b]) =>
    names.some(n => n.includes(a)) && names.some(n => n.includes(b))
  );
};

const Discover = () => {
  const navigate = useNavigate();
  const { t: tr } = useLanguage();

  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 348;
  const [toolOfTheWeek, setToolOfTheWeek] = useState(null);
  const [toolLoading, setToolLoading] = useState(true);
  const [toolError, setToolError] = useState(null);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [catalogTools, setCatalogTools] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogIndustry, setCatalogIndustry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stackTools, setStackTools] = useState([]);

  // Build translated module-level constants inside the component
  const ENGINE_SIGNALS = [
    {
      icon: Rss,
      label: tr('discover.engineRssLabel'),
      desc: tr('discover.engineRssDesc'),
      color: 'text-blue-400',
      bg: 'bg-blue-500/10 border-blue-500/20',
    },
    {
      icon: Database,
      label: tr('discover.engineDataLabel'),
      desc: tr('discover.engineDataDesc'),
      color: 'text-purple-400',
      bg: 'bg-purple-500/10 border-purple-500/20',
    },
    {
      icon: Brain,
      label: tr('discover.engineMatchLabel'),
      desc: tr('discover.engineMatchDesc'),
      color: 'text-[#3b82f6]',
      bg: 'bg-blue-500/10 border-blue-500/20',
    },
    {
      icon: BarChart2,
      label: tr('discover.engineRoiLabel'),
      desc: tr('discover.engineRoiDesc'),
      color: 'text-amber-400',
      bg: 'bg-amber-500/10 border-amber-500/20',
    },
  ];

  const STACK_TEMPLATES = [
    {
      label: tr('discover.tplRestaurantLabel'),
      emoji: '🍽️',
      tools: [
        { id: 'tpl_opentable', name: 'OpenTable SMS', category: 'Reservations', smb_score: 9, complexity: 'low', pricing_band: '$39/mo', adoption_trend: 'growing', why: tr('discover.tplRestaurantT1Why') },
        { id: 'tpl_manychat', name: 'ManyChat', category: 'Messaging', smb_score: 8, complexity: 'low', pricing_band: '$15/mo', adoption_trend: 'growing', why: tr('discover.tplRestaurantT2Why') },
        { id: 'tpl_mailchimp', name: 'Mailchimp', category: 'Email', smb_score: 8, complexity: 'low', pricing_band: 'Free–$13/mo', adoption_trend: 'stable', why: tr('discover.tplRestaurantT3Why') },
      ],
    },
    {
      label: tr('discover.tplEcomLabel'),
      emoji: '🛒',
      tools: [
        { id: 'tpl_klaviyo', name: 'Klaviyo', category: 'Email / SMS', smb_score: 9, complexity: 'medium', pricing_band: 'Free–$45/mo', adoption_trend: 'growing', why: tr('discover.tplEcomT1Why') },
        { id: 'tpl_tidio', name: 'Tidio', category: 'Live Chat + AI', smb_score: 8, complexity: 'low', pricing_band: 'Free–$29/mo', adoption_trend: 'growing', why: tr('discover.tplEcomT2Why') },
        { id: 'tpl_postscript', name: 'Postscript', category: 'SMS Marketing', smb_score: 8, complexity: 'low', pricing_band: 'Usage-based', adoption_trend: 'growing', why: tr('discover.tplEcomT3Why') },
      ],
    },
    {
      label: tr('discover.tplServiceLabel'),
      emoji: '🤝',
      tools: [
        { id: 'tpl_hubspot', name: 'HubSpot CRM', category: 'CRM', smb_score: 9, complexity: 'medium', pricing_band: 'Free', adoption_trend: 'growing', why: tr('discover.tplServiceT1Why') },
        { id: 'tpl_calendly', name: 'Calendly', category: 'Scheduling', smb_score: 9, complexity: 'low', pricing_band: 'Free–$10/mo', adoption_trend: 'growing', why: tr('discover.tplServiceT2Why') },
        { id: 'tpl_zapier', name: 'Zapier', category: 'Automation', smb_score: 8, complexity: 'medium', pricing_band: 'Free–$20/mo', adoption_trend: 'growing', why: tr('discover.tplServiceT3Why') },
      ],
    },
    {
      label: tr('discover.tplDentalLabel'),
      emoji: '🦷',
      tools: [
        { id: 'tpl_weave', name: 'Weave', category: 'Patient Comms Hub', smb_score: 9, complexity: 'low', pricing_band: '$199–399/mo', adoption_trend: 'growing', why: tr('discover.tplDentalT1Why') },
        { id: 'tpl_hatch_dental', name: 'Hatch', category: 'AI Receptionist', smb_score: 9, complexity: 'low', pricing_band: '$149–329/mo', adoption_trend: 'growing', why: tr('discover.tplDentalT2Why') },
        { id: 'tpl_revenuewell', name: 'RevenueWell', category: 'Recall Campaigns', smb_score: 8, complexity: 'medium', pricing_band: '$329/mo', adoption_trend: 'stable', why: tr('discover.tplDentalT3Why') },
      ],
    },
    {
      label: tr('discover.tplLegalLabel'),
      emoji: '⚖️',
      tools: [
        { id: 'tpl_clio', name: 'Clio', category: 'Practice Management', smb_score: 9, complexity: 'medium', pricing_band: '$39–129/user', adoption_trend: 'growing', why: tr('discover.tplLegalT1Why') },
        { id: 'tpl_smithai', name: 'Smith.ai', category: 'AI + Human Receptionist', smb_score: 9, complexity: 'low', pricing_band: '$200–500/mo', adoption_trend: 'growing', why: tr('discover.tplLegalT2Why') },
        { id: 'tpl_spellbook', name: 'Spellbook', category: 'AI Contract Review', smb_score: 8, complexity: 'low', pricing_band: '$129–249/user', adoption_trend: 'growing', why: tr('discover.tplLegalT3Why') },
      ],
    },
    {
      label: tr('discover.tplAccountingLabel'),
      emoji: '📊',
      tools: [
        { id: 'tpl_qbo', name: 'QuickBooks Online', category: 'Bookkeeping', smb_score: 10, complexity: 'low', pricing_band: '$30–200/mo', adoption_trend: 'stable', why: tr('discover.tplAccountingT1Why') },
        { id: 'tpl_dext', name: 'Dext', category: 'Receipt + Bill Capture', smb_score: 9, complexity: 'low', pricing_band: '$24–60/mo', adoption_trend: 'growing', why: tr('discover.tplAccountingT2Why') },
        { id: 'tpl_karbon', name: 'Karbon', category: 'Client Workflow', smb_score: 8, complexity: 'medium', pricing_band: '$79–149/user', adoption_trend: 'growing', why: tr('discover.tplAccountingT3Why') },
      ],
    },
    {
      label: tr('discover.tplHvacLabel'),
      emoji: '🔧',
      tools: [
        { id: 'tpl_servicetitan', name: 'ServiceTitan', category: 'Field Service Mgmt', smb_score: 9, complexity: 'high', pricing_band: '$125–185/tech', adoption_trend: 'growing', why: tr('discover.tplHvacT1Why') },
        { id: 'tpl_goodcall', name: 'Goodcall', category: 'AI Phone Agent', smb_score: 8, complexity: 'low', pricing_band: '$79–299/mo', adoption_trend: 'growing', why: tr('discover.tplHvacT2Why') },
        { id: 'tpl_nicejob', name: 'NiceJob', category: 'Review Collection', smb_score: 8, complexity: 'low', pricing_band: '$75–150/mo', adoption_trend: 'growing', why: tr('discover.tplHvacT3Why') },
      ],
    },
    {
      label: tr('discover.tplMedSpaLabel'),
      emoji: '💅',
      tools: [
        { id: 'tpl_boulevard', name: 'Boulevard', category: 'Salon/Spa Platform', smb_score: 9, complexity: 'medium', pricing_band: '$175–395/mo', adoption_trend: 'growing', why: tr('discover.tplMedSpaT1Why') },
        { id: 'tpl_hatch_spa', name: 'Hatch', category: 'AI Receptionist', smb_score: 9, complexity: 'low', pricing_band: '$149–329/mo', adoption_trend: 'growing', why: tr('discover.tplMedSpaT2Why') },
        { id: 'tpl_phorest', name: 'Phorest', category: 'Marketing + Retention', smb_score: 8, complexity: 'medium', pricing_band: '$129–249/mo', adoption_trend: 'growing', why: tr('discover.tplMedSpaT3Why') },
      ],
    },
  ];

  const CATALOG_INDUSTRIES = [
    { label: tr('discover.catalogIndustryAll'), value: null },
    { label: tr('discover.catalogIndustryRetail'), value: 'retail' },
    { label: tr('discover.catalogIndustryHealthcare'), value: 'healthcare' },
    { label: tr('discover.catalogIndustryFinance'), value: 'finance' },
    { label: tr('discover.catalogIndustryHospitality'), value: 'hospitality' },
    { label: tr('discover.catalogIndustryManufacturing'), value: 'manufacturing' },
    { label: tr('discover.catalogIndustryLegal'), value: 'legal' },
  ];

  // Match by id OR name (handles template IDs vs API MongoDB IDs)
  const toolKey = (t) => (t.id || t.name || '').toLowerCase();
  const isInStack = (tool) => stackTools.some(s => toolKey(s) === toolKey(tool) || s.name?.toLowerCase() === tool.name?.toLowerCase());

  const toggleStack = (tool) => {
    if (isInStack(tool)) {
      setStackTools(prev => prev.filter(t => toolKey(t) !== toolKey(tool)));
    } else {
      setStackTools(prev => {
        if (prev.length >= 3) { toast.error(tr('discover.stackFullToast')); return prev; }
        return [...prev, tool];
      });
    }
  };

  const calcImpact = (tools) => {
    if (!tools.length) return null;
    const hrsMap = { 1: 0.5, 2: 0.5, 3: 1, 4: 1, 5: 1.5, 6: 1.5, 7: 2, 8: 3, 9: 5, 10: 7 };
    const totalHrs = tools.reduce((sum, t) => sum + (hrsMap[Math.round(t.smb_score)] ?? 2), 0);
    const value12w = Math.round(totalHrs * 35 * 12 * 0.8);
    const avgScore = (tools.reduce((s, t) => s + (t.smb_score ?? 7), 0) / tools.length).toFixed(1);
    const complexityRank = { low: 1, medium: 2, high: 3 };
    const minComplexity = tools.reduce((min, t) => {
      const rank = complexityRank[t.complexity?.toLowerCase()] ?? 2;
      return rank < complexityRank[min] ? t.complexity?.toLowerCase() : min;
    }, 'high');
    return { totalHrs, value12w, avgScore, complexity: minComplexity };
  };

  const translateComplexity = (lvl) => {
    const key = (lvl || '').toLowerCase();
    if (key === 'low') return tr('discover.stackComplexityLow');
    if (key === 'medium') return tr('discover.stackComplexityMedium');
    if (key === 'high') return tr('discover.stackComplexityHigh');
    return lvl ? lvl.charAt(0).toUpperCase() + lvl.slice(1) : '';
  };

  const confettiRef = useRef(null);

  useEffect(() => {
    const fetchStats = fetch(`${API_BASE}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});

    const fetchTool = fetch(`${API_BASE}/api/audit/tool-of-the-week`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setToolOfTheWeek(d.tool || null))
      .catch((err) => { setToolError(err?.message || 'Failed to load tool of the week'); setToolOfTheWeek(null); })
      .finally(() => setToolLoading(false));

    const fetchCandidates = fetch(`${API_BASE}/api/discover/candidates?limit=5&min_score=7`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setCandidates(d.candidates || []))
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));

    Promise.all([fetchStats, fetchTool, fetchCandidates]);
  }, []);

  useEffect(() => {
    if (stackTools.length === 3 && confettiRef.current) {
      confetti(confettiRef.current, {
        angle: 90,
        spread: 70,
        startVelocity: 40,
        elementCount: 80,
        colors: ['#3b82f6', '#3b82f6', '#a855f7', '#f59e0b', '#ffffff'],
        duration: 2800,
      });
    }
  }, [stackTools.length]);

  const handleJoinDiscover = async (e) => {
    e.preventDefault();
    const form = e.target;
    const email = form.elements.email?.value;
    if (!email) return;

    try {
      setSubmittingEmail(true);
      const res = await fetch(`${API_BASE}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'discover' }),
      });
      if (!res.ok) throw new Error('Failed to subscribe');
      toast.success(tr('discover.emailSuccessToast'));
      form.reset();
    } catch (err) {
      console.error('Discover newsletter error:', err);
      toast.error(tr('discover.emailErrorToast'));
    } finally {
      setSubmittingEmail(false);
    }
  };


  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setCatalogLoading(true);
        const qs = catalogIndustry ? `?industry=${catalogIndustry}&limit=12` : '?limit=12';
        const res = await fetch(`${API_BASE}/api/tools/catalog${qs}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCatalogTools(data.tools || []);
      } catch {
        setCatalogTools([]);
      } finally {
        setCatalogLoading(false);
      }
    };
    fetchCatalog();
  }, [catalogIndustry]);

  const tool = toolOfTheWeek;

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Dot grid bg */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}
          aria-hidden="true"
        />
        {/* Orb */}
        <motion.div
          className="absolute top-20 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-10"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
          aria-hidden="true"
        />

        <div className="max-w-4xl mx-auto text-center relative">
          <Badge className="mb-4 bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-4 py-1.5">
            <Sparkles className="w-3.5 h-3.5 mr-1.5 inline" />
            {tr('discover.heroBadge')}
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-5 tracking-tight">
            {tr('discover.heroTitleLine1')}
            <br />
            <span className="bg-gradient-to-r from-[#3b82f6] to-blue-400 bg-clip-text text-transparent">
              {tr('discover.heroTitleLine2')}
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            {tr('discover.heroSubtitle', { count: displayCount })}
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {[
              { icon: Rss, label: tr('discover.heroPillSignals') },
              { icon: Database, label: tr('discover.heroPillTools', { count: displayCount }) },
              { icon: Brain, label: tr('discover.heroPillMatching') },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-slate-400 bg-slate-950/50 px-3 py-1.5 rounded-full border border-white/10">
                <Icon className="w-3.5 h-3.5 text-[#3b82f6]" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-5xl mx-auto">

          {/* ── BROWSE BY INDUSTRY ───────────────────────────────────────
               Internal links to the per-industry SEO catalog pages
               (/discover/dental, /discover/real-estate, etc). Two purposes:
                 1. UX — buyers can drill into their vertical's curated list.
                 2. SEO — Google follows internal links to find + crawl all
                    18 indexable industry pages registered in sitemap.xml.

               Round-26 redesign (user feedback "all industries under browse
               looks ugly"): replaced the 18-button 2/3/4-col grid that ate
               half the page on mobile with a compact horizontal-scrolling
               pill bar. Same 18 links, ~80% less vertical real estate, and
               native horizontal scroll on phones via overflow-x-auto. SEO
               link graph fully preserved — Google still discovers every
               industry page from this hub. */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-5 h-5 text-[#3b82f6]" />
              <h2 className="text-xl font-bold text-white">Filter by industry</h2>
              <div className="flex-1 h-px bg-white/8 ml-2" />
            </div>
            <p className="text-sm text-slate-400 mb-4 max-w-2xl">
              Each list is scored by our matching engine against the specific problems and tools that industry actually faces.
            </p>
            {/* Horizontal scroll pill bar — mobile and desktop both get
                the same compact strip. scrollbar-hide keeps the chrome
                clean; pills snap to natural reading order. */}
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
              {[
                { url: 'real-estate',           label: 'Real Estate' },
                { url: 'dental',                label: 'Dental' },
                { url: 'hvac',                  label: 'HVAC' },
                { url: 'legal',                 label: 'Legal' },
                { url: 'accounting',            label: 'Accounting' },
                { url: 'healthcare',            label: 'Healthcare' },
                { url: 'restaurants',           label: 'Restaurants' },
                { url: 'retail',                label: 'Retail' },
                { url: 'ecommerce',             label: 'E-commerce' },
                { url: 'professional-services', label: 'Pro Services' },
                { url: 'technology',            label: 'Technology' },
                { url: 'finance',               label: 'Finance' },
                { url: 'manufacturing',         label: 'Manufacturing' },
                { url: 'education',             label: 'Education' },
                { url: 'construction',          label: 'Construction' },
                { url: 'auto-repair',           label: 'Auto Repair' },
                { url: 'med-spa',               label: 'Med Spa' },
                { url: 'saas',                  label: 'SaaS' },
              ].map(({ url, label }) => (
                <Link
                  key={url}
                  to={`/discover/${url}`}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 bg-slate-900/60 hover:bg-blue-500/10 border border-white/10 hover:border-blue-500/40 rounded-full px-3.5 py-1.5 transition-all"
                >
                  <span className="text-xs font-medium text-slate-300 hover:text-white whitespace-nowrap">{label}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* ── TOOL OF THE WEEK CARD ──────────────────────────── */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-5 h-5 text-[#3b82f6]" />
              <h2 className="text-xl font-bold text-white">{tr('discover.pickSectionTitle')}</h2>
              <div className="flex-1 h-px bg-white/8 ml-2" />
            </div>

            {/* Loading */}
            {toolLoading && (
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 max-w-3xl">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-20 h-20 rounded-full bg-white/10 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-6 bg-white/10 rounded animate-pulse w-2/3" />
                      <div className="h-4 bg-white/10 rounded animate-pulse w-1/3" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[80, 60, 90].map((w) => (
                      <div key={w} className={`h-3 bg-white/10 rounded animate-pulse`} style={{ width: `${w}%` }} />
                    ))}
                  </div>
                  <p className="text-xs text-slate-600 mt-4">{tr('discover.pickLoading')}</p>
                </CardContent>
              </Card>
            )}

            {/* Error */}
            {!toolLoading && toolError && (
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 max-w-3xl">
                <CardContent className="p-8">
                  <p className="text-slate-400 mb-4">{tr('discover.pickErrorBody')}</p>
                  <Button variant="outline" className="border-blue-500/60 text-[#3b82f6] hover:bg-blue-500/10" onClick={() => navigate('/audit')}>
                    {tr('discover.pickErrorCta')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* No tool */}
            {!toolLoading && !toolError && !tool && (
              <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 max-w-3xl">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-2">{tr('discover.pickEmptyTitle')}</h3>
                  <p className="text-slate-400">{tr('discover.pickEmptyBody')}</p>
                </CardContent>
              </Card>
            )}

            {/* Tool card */}
            {!toolLoading && !toolError && tool && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-3xl"
              >
                {/* Gradient glow border */}
                <div className="relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-br from-blue-500/40 via-blue-500/20 to-blue-500/40 rounded-2xl blur-sm" aria-hidden="true" />
                  <Card className="relative bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">

                    {/* Top bar */}
                    <div className="bg-blue-500/10 border-b border-blue-500/20 px-6 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#3b82f6] animate-pulse" />
                        <span className="text-[#3b82f6] text-xs font-semibold uppercase tracking-wider">
                          {tr('discover.pickWeek', { week: tool.week_number || '—' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {tool.adoption_trend === 'growing' && (
                          <Badge className="bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 text-xs flex items-center gap-1 py-0.5">
                            <TrendingUp className="w-3 h-3" /> {tr('discover.pickTrendingUp')}
                          </Badge>
                        )}
                        <Badge className="bg-white/8 text-slate-400 border-0 text-xs py-0.5">{tr('discover.pickUpdatedMonday')}</Badge>
                      </div>
                    </div>

                    <CardContent className="p-6 sm:p-8">
                      {/* Tool name + score ring */}
                      <div className="flex items-start justify-between gap-4 mb-6">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1 leading-tight">
                            {tool.tool_name}
                          </h3>
                          {tool.category && (
                            <p className="text-[#3b82f6] text-sm font-medium">{tool.category}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <ScoreRing score={tool.smb_score ?? 7} max={10} />
                          <span className="text-[10px] text-slate-500 font-medium">{tr('discover.pickSmbScoreLabel')}</span>
                        </div>
                      </div>

                      {/* Why this week — engine editorial */}
                      {tool.why_this_week && (
                        <div className="bg-slate-950/50 rounded-xl p-4 mb-5 border border-white/15">
                          <p className="text-xs font-semibold text-[#3b82f6] uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <Brain className="w-3.5 h-3.5" /> {tr('discover.pickEngineTake')}
                          </p>
                          <p className="text-slate-200 leading-relaxed text-sm">{tool.why_this_week}</p>
                        </div>
                      )}

                      {/* Why / description */}
                      {tool.why && !tool.why_this_week && (
                        <p className="text-slate-300 mb-5 leading-relaxed">{tool.why}</p>
                      )}

                      {/* Key metrics row */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
                        {[
                          {
                            label: tr('discover.pickMetricPricing'),
                            value: tool.pricing_band || tr('discover.pickMetricPricingDefault'),
                            icon: DollarSign,
                            color: 'text-[#3b82f6]',
                          },
                          {
                            label: tr('discover.pickMetricSetup'),
                            value: tool.integrationComplexity || tr('discover.pickMetricSetupDefault'),
                            icon: Layers,
                            color: 'text-blue-400',
                          },
                          tool.typical_setup_hours && {
                            label: tr('discover.pickMetricSetupTime'),
                            value: `~${tool.typical_setup_hours}h`,
                            icon: Clock,
                            color: 'text-purple-400',
                          },
                          tool.roi_pct && {
                            label: tr('discover.pickMetricClaimedRoi'),
                            value: `${Math.round(tool.roi_pct)}%`,
                            icon: BarChart2,
                            color: 'text-amber-400',
                          },
                          tool.signal_count > 0 && {
                            label: tr('discover.pickMetricLiveSignals'),
                            value: `${tool.signal_count}`,
                            icon: Rss,
                            color: 'text-pink-400',
                          },
                        ]
                          .filter(Boolean)
                          .slice(0, 4)
                          .map(({ label, value, icon: Icon, color }) => (
                            <div key={label} className="bg-slate-950/50 border border-white/15 rounded-lg p-3 text-center">
                              <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
                              <div className={`text-sm font-bold ${color}`}>{value}</div>
                              <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
                            </div>
                          ))}
                      </div>

                      {/* Success story */}
                      {tool.successStory && (
                        <blockquote className="border-l-2 border-blue-500/40 pl-4 mb-5 italic text-slate-400 text-sm leading-relaxed">
                          {tool.successStory}
                        </blockquote>
                      )}

                      {/* Role */}
                      <p className="text-xs text-slate-500 mb-5">
                        {tr('discover.pickBestFor')} <span className="text-slate-400">{tool.owner_role || tr('discover.pickDefaultRole')}</span>
                      </p>

                      {/* CTAs */}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-[#3b82f6] hover:to-blue-400 text-white rounded-full"
                          onClick={() => navigate('/audit')}
                        >
                          {tr('discover.pickCtaFit')}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          className="border-white/15 text-slate-300 hover:bg-white/5 hover:border-white/25"
                          onClick={() => navigate('/audit')}
                        >
                          {tr('discover.pickCtaBlueprint')}
                        </Button>
                        <a
                          href={buildDiscoverLaunchUrl(tool, tool.affiliate_url || tool.website_url) || `https://www.google.com/search?q=${encodeURIComponent(tool.tool_name || tool.name || '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 hover:border-blue-500/60 text-sm font-medium transition-colors"
                        >
                          {(tool.affiliate_url || tool.website_url) ? tr('discover.catalogLinkVisit') : tr('discover.catalogLinkSearch')}
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            )}
          </div>

          {/* ── ENGINE METHODOLOGY STRIP ───────────────────────── */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-6">
              <Radar className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-white">{tr('discover.engineTitle')}</h2>
              <div className="flex-1 h-px bg-white/8 ml-2" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ENGINE_SIGNALS.map(({ icon: Icon, label, desc, color, bg }) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className={`rounded-xl p-4 border ${bg}`}
                >
                  <Icon className={`w-6 h-6 ${color} mb-3`} />
                  <p className={`text-sm font-semibold ${color} mb-1.5`}>{label}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-xs text-slate-600 mt-4 text-center">
              {tr('discover.engineFootnote')}
            </p>
          </div>

          {/* ── RECENTLY DISCOVERED ─────────────────────────────── */}
          {!candidatesLoading && candidates.length > 0 && (
            <div className="mb-16">
              <div className="flex items-center gap-2 mb-5">
                <Rss className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-bold text-white">{tr('discover.recentTitle')}</h2>
                <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/20 text-xs ml-1">{tr('discover.recentBadge')}</Badge>
                <div className="flex-1 h-px bg-white/8 ml-2" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {candidates.map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-blue-500/40 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="font-semibold text-white text-sm leading-tight">{c.tool_name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <div className="w-16 bg-slate-950/50 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(c.smb_suitability_score / 10) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-[#3b82f6] font-bold w-6 text-right">
                          {c.smb_suitability_score}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mb-2 leading-relaxed">{c.description}</p>
                    {c.key_strength && (
                      <p className="text-xs text-blue-300/80 italic mb-2">{c.key_strength}</p>
                    )}
                    <div className="flex flex-wrap gap-1 items-center">
                      {c.signal_type && c.signal_type !== 'other' && (
                        <Badge className="bg-white/8 text-slate-300 border-0 text-xs py-0">{c.signal_type}</Badge>
                      )}
                      {c.pricing_band && c.pricing_band !== 'unknown' && (
                        <Badge className="bg-white/8 text-slate-300 border-0 text-xs py-0">{c.pricing_band}</Badge>
                      )}
                      {c.claimed_roi_percent && (
                        <Badge className="bg-blue-500/15 text-[#3b82f6] border-0 text-xs py-0">
                          {tr('discover.recentRoiClaimed', { pct: c.claimed_roi_percent })}
                        </Badge>
                      )}
                      <a
                        href={buildDiscoverLaunchUrl(c, c.website_url) || `https://www.google.com/search?q=${encodeURIComponent(c.tool_name || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        {c.website_url ? tr('discover.catalogLinkVisit') : tr('discover.catalogLinkSearch')}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* ── TOOL CATALOG ────────────────────────────────────── */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-5">
              <BookOpen className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">{tr('discover.catalogTitle')}</h2>
              <Badge className="bg-slate-950/50 text-slate-300 border border-white/10 text-xs ml-1">{tr('discover.catalogBadge')}</Badge>
              <div className="flex-1 h-px bg-white/8 ml-2" />
            </div>

            {/* Text search */}
            <div className="mb-4">
              <Input
                placeholder={tr('discover.catalogSearchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-slate-950/50 border-white/10 text-white placeholder:text-slate-500 max-w-xs"
              />
            </div>

            {/* Industry filter tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {CATALOG_INDUSTRIES.map((ind) => (
                <button
                  key={ind.label}
                  onClick={() => setCatalogIndustry(ind.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    catalogIndustry === ind.value
                      ? 'bg-blue-500 text-white shadow-sm shadow-blue-500/30'
                      : 'bg-slate-950/50 text-slate-400 border border-white/10 hover:border-blue-500/50 hover:text-white'
                  }`}
                >
                  {ind.label}
                </button>
              ))}
            </div>

            {catalogLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-slate-900/70 border border-white/10 rounded-xl p-4 animate-pulse">
                    <div className="h-4 bg-white/10 rounded w-2/3 mb-2" />
                    <div className="h-3 bg-white/10 rounded w-1/3 mb-3" />
                    <div className="h-3 bg-white/10 rounded mb-1" />
                    <div className="h-3 bg-white/10 rounded w-4/5" />
                  </div>
                ))}
              </div>
            ) : catalogTools.length === 0 ? (
              <p className="text-slate-500 text-sm">{tr('discover.catalogEmpty')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {catalogTools.filter(t =>
                  !searchQuery || t.name?.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((t, i) => (
                  <motion.div
                    key={t.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (i % 6) * 0.05 }}
                    className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-blue-500/40 hover:-translate-y-0.5 transition-all flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className="font-semibold text-white text-sm leading-tight line-clamp-2 min-w-0 flex-1" title={t.name}>{t.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1">
                          <div className="w-12 bg-slate-950/50 rounded-full h-1.5 overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(t.smb_score / 10) * 100}%` }} />
                          </div>
                          <span className="text-xs text-[#3b82f6] font-bold w-5 text-right">{t.smb_score}</span>
                        </div>
                        <button
                          onClick={() => toggleStack(t)}
                          title={isInStack(t) ? tr('discover.catalogRemoveFromStack') : tr('discover.catalogAddToStack')}
                          aria-label={isInStack(t) ? tr('discover.catalogRemoveAria', { name: t.name }) : tr('discover.catalogAddAria', { name: t.name })}
                          className={`w-6 h-6 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${
                            isInStack(t)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/8 text-slate-400 hover:bg-blue-500/30 hover:text-[#3b82f6] border border-white/10'
                          }`}
                        >
                          {isInStack(t)
                            ? <CheckCircle2 className="w-3.5 h-3.5" />
                            : <Plus className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-blue-300 mb-2 truncate" title={t.category}>{t.category}</p>
                    {t.why && (
                      <p className="text-xs text-slate-400 mb-3 leading-relaxed flex-1 line-clamp-3">{t.why}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-auto">
                      {t.adoption_trend === 'growing' && (
                        <Badge className="bg-blue-500/15 text-[#3b82f6] border-0 text-xs py-0 flex items-center gap-1">
                          <TrendingUp className="w-2.5 h-2.5" /> {tr('discover.catalogBadgeGrowing')}
                        </Badge>
                      )}
                      {t.pricing_band && t.pricing_band !== 'Varies' && (
                        <Badge className="bg-white/8 text-slate-300 border-0 text-xs py-0">{t.pricing_band}</Badge>
                      )}
                      <Badge className="bg-white/8 text-slate-300 border-0 text-xs py-0">
                        {tr('discover.catalogBadgeSetup', { level: translateComplexity(t.complexity) })}
                      </Badge>
                      {t.signal_count > 0 && (
                        <Badge className="bg-blue-500/15 text-blue-400 border-0 text-xs py-0">
                          {t.signal_count !== 1
                            ? tr('discover.catalogBadgeSignalsPlural', { count: t.signal_count })
                            : tr('discover.catalogBadgeSignals', { count: t.signal_count })}
                        </Badge>
                      )}
                      <a
                        href={buildDiscoverLaunchUrl(t, t.affiliate_url || t.website_url) || `https://www.google.com/search?q=${encodeURIComponent(t.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                        onClick={e => e.stopPropagation()}
                      >
                        {(t.affiliate_url || t.website_url) ? tr('discover.catalogLinkVisit') : tr('discover.catalogLinkSearch')} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* CTA — convert browsers to audit */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-blue-600/20 via-blue-500/15 to-purple-600/20 border border-blue-500/30 rounded-2xl p-6 text-center"
            >
              <h3 className="text-lg font-bold text-white mb-2">
                {tr('discover.catalogCtaTitle', { count: displayCount })}
              </h3>
              <p className="text-sm text-slate-400 mb-4 max-w-xl mx-auto">
                {tr('discover.catalogCtaBody')}
              </p>
              <button
                onClick={() => navigate('/audit')}
                className="inline-flex items-center gap-2 bg-gradient-to-br from-[#3b82f6] to-blue-600 text-white font-bold px-6 py-3 rounded-full text-sm shadow-lg shadow-blue-500/20 hover:scale-[1.03] active:scale-[0.97] transition-transform"
              >
                {tr('discover.catalogCtaButton')} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-slate-500 mt-3">{tr('discover.catalogCtaFine')}</p>
            </motion.div>
          </div>

          {/* ── PERSONALIZED NUDGE ─────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 bg-gradient-to-r from-blue-500/8 via-blue-500/5 to-purple-500/8 border border-blue-500/15 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6"
          >
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-base mb-1">{tr('discover.nudgeTitle')}</p>
              <p className="text-slate-400 text-sm leading-relaxed">
                {tr('discover.nudgeBody')}
              </p>
            </div>
            <Button
              className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-[#3b82f6] hover:to-blue-400 text-white rounded-full whitespace-nowrap shrink-0"
              onClick={() => navigate('/audit')}
            >
              {tr('discover.nudgeCta')} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>

          {/* ── BUILD YOUR STACK ────────────────────────────────── */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-5">
              <Layers className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-bold text-white">{tr('discover.stackTitle')}</h2>
              <Badge className={`text-xs ml-1 border ${stackTools.length === 3 ? 'bg-blue-500/20 text-[#3b82f6] border-blue-500/30' : 'bg-slate-950/50 text-slate-300 border-white/10'}`}>
                {tr('discover.stackSelected', { count: stackTools.length })}
              </Badge>
              {stackTools.length === 3 && (
                <span ref={confettiRef}>
                  <Badge className="bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 text-xs animate-pulse">
                    {tr('discover.stackComplete')}
                  </Badge>
                </span>
              )}
              <div className="flex-1 h-px bg-white/8 ml-2" />
            </div>

            {/* Templates */}
            <div className="flex flex-wrap gap-2 mb-5">
              <span className="text-xs text-slate-500 self-center mr-1">{tr('discover.stackQuickStart')}</span>
              {STACK_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  onClick={() => setStackTools(tpl.tools)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-950/50 border border-white/10 text-slate-300 hover:border-purple-500/50 hover:text-white hover:bg-purple-500/10 transition-all"
                >
                  <span>{tpl.emoji}</span> {tpl.label}
                </button>
              ))}
            </div>

            {stackTools.length === 0 ? (
              <div className="border-2 border-dashed border-white/10 rounded-2xl p-10 text-center">
                <Layers className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">{tr('discover.stackEmptyLine1Prefix')} <span className="text-[#3b82f6]">+</span> {tr('discover.stackEmptyLine1Suffix')}</p>
                <p className="text-slate-600 text-xs mt-1">{tr('discover.stackEmptyLine2')}</p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={stackTools.map(t => t.id).join('-')}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6 space-y-6"
                >
                  {/* Synergy badge */}
                  {hasSynergy(stackTools) && (
                    <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
                      <p className="text-xs text-blue-300">{tr('discover.stackSynergy')}</p>
                    </div>
                  )}

                  {/* Category diversity nudge */}
                  {stackTools.length >= 2 && (() => {
                    const cats = stackTools.map(t => t.category?.toLowerCase());
                    const allSame = cats.every(c => c === cats[0]);
                    if (!allSame) return null;
                    return (
                      <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                        <p className="text-xs text-amber-300">{tr('discover.stackDiversityWarning')}</p>
                      </div>
                    );
                  })()}

                  {/* Phase slots */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[tr('discover.stackPhase1'), tr('discover.stackPhase2'), tr('discover.stackPhase3')].map((phase, i) => {
                      const tool = stackTools[i];
                      return (
                        <motion.div
                          key={phase}
                          layout
                          className={`rounded-xl p-4 border-2 transition-all ${tool ? 'border-blue-500/40 bg-blue-500/5' : 'border-dashed border-white/10 bg-slate-950/30'}`}
                        >
                          <p className="text-xs text-slate-500 font-medium mb-2">{phase}</p>
                          {tool ? (
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-white truncate">{tool.name}</p>
                                <p className="text-xs text-blue-300/70">{tool.category}</p>
                              </div>
                              <button
                                onClick={() => toggleStack(tool)}
                                aria-label={tr('discover.catalogRemoveAria', { name: tool.name })}
                                className="w-5 h-5 rounded-full bg-white/8 hover:bg-red-500/20 flex items-center justify-center text-slate-500 hover:text-red-400 transition-all flex-shrink-0"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-600 italic">{tr('discover.stackSlotEmpty')}</p>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Impact panel */}
                  {(() => {
                    const impact = calcImpact(stackTools);
                    if (!impact) return null;
                    return (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-3">{tr('discover.stackImpactTitle')}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: tr('discover.stackImpactHours'), value: `~${impact.totalHrs}h`, icon: Clock, color: 'text-[#3b82f6]' },
                            { label: tr('discover.stackImpactValue'), value: <span title={tr('discover.stackImpactValueTooltip')}>${impact.value12w.toLocaleString()}</span>, icon: DollarSign, color: 'text-blue-400' },
                            { label: tr('discover.stackImpactScore'), value: `${impact.avgScore}/10`, icon: BarChart2, color: 'text-purple-400' },
                            { label: tr('discover.stackImpactComplexity'), value: translateComplexity(impact.complexity), icon: Zap, color: 'text-amber-400' },
                          ].map(({ label, value, icon: Icon, color }) => (
                            <motion.div
                              key={label}
                              initial={{ opacity: 0, scale: 0.92 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.25 }}
                              className="bg-slate-950/50 border border-white/8 rounded-xl p-3 text-center"
                            >
                              <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
                              <div className={`text-base font-bold ${color}`}>{value}</div>
                              <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{label}</div>
                            </motion.div>
                          ))}
                        </div>
                        <p className="text-xs text-slate-600 mt-3">
                          {tr('discover.stackImpactNote')}
                        </p>
                      </div>
                    );
                  })()}

                  <div className="pt-2 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-sm text-slate-400">{tr('discover.stackFinalPrompt')}</p>
                    <Button
                      className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-[#3b82f6] hover:to-blue-400 text-white rounded-full shrink-0"
                      onClick={() => navigate('/audit')}
                    >
                      {tr('discover.stackFinalCta')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {/* ── VALUE PROPS ─────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Zap,
                title: tr('discover.valuePropsTitle1'),
                desc: tr('discover.valuePropsDesc1', { count: displayCount }),
                color: 'text-[#3b82f6]',
                bg: 'bg-blue-500/10',
              },
              {
                icon: TrendingUp,
                title: tr('discover.valuePropsTitle2'),
                desc: tr('discover.valuePropsDesc2'),
                color: 'text-blue-400',
                bg: 'bg-blue-500/10',
              },
              {
                icon: ShieldCheck,
                title: tr('discover.valuePropsTitle3'),
                desc: tr('discover.valuePropsDesc3', { count: displayCount }),
                color: 'text-purple-400',
                bg: 'bg-purple-500/10',
              },
            ].map(({ icon: Icon, title, desc, color, bg }) => (
              <Card
                key={title}
                className="bg-slate-900/70 backdrop-blur-xl border border-white/10 hover:border-blue-500/40 transition-all cursor-pointer group"
                onClick={() => navigate('/audit')}
              >
                <CardContent className="p-6">
                  <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <h3 className="font-bold text-white mb-2">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{desc}</p>
                  <div className={`flex items-center ${color} text-sm font-semibold group-hover:gap-2 transition-all`}>
                    {tr('discover.valuePropsCta')} <ArrowRight className="ml-1.5 w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── EMAIL CAPTURE ────────────────────────────────────── */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/70 to-slate-950/70 backdrop-blur-xl border border-white/10 p-8 md:p-12 text-center">
            <motion.div
              className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 6, repeat: Infinity }}
              aria-hidden="true"
            />
            <div className="relative">
              <CheckCircle2 className="w-10 h-10 text-[#3b82f6] mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">{tr('discover.emailTitle')}</h3>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                {tr('discover.emailBody')}
              </p>
              <form
                onSubmit={handleJoinDiscover}
                className="flex flex-col sm:flex-row gap-3 justify-center"
              >
                <Input
                  type="email"
                  name="email"
                  required
                  placeholder={tr('discover.emailPlaceholder')}
                  className="bg-slate-950/50 border-white/15 text-white max-w-xs placeholder:text-slate-500"
                />
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-[#3b82f6] hover:to-blue-400 text-white rounded-full"
                  disabled={submittingEmail}
                >
                  {submittingEmail ? tr('discover.emailSubmitting') : tr('discover.emailSubmit')}
                </Button>
              </form>
              <p className="text-xs text-slate-600 mt-3">
                {tr('discover.emailFine')}
              </p>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Discover;
